import { ed, svgText, ticks, niceMax, clip } from "./tokens";
import { useTooltip } from "./useTooltip";

export interface DumbbellRow {
  id: string;
  label: string;
  from: number | null;
  to: number;
  /** Color this row: "emph" for the gain the figure calls out, "caution" for the drop. */
  tone?: "emph" | "caution";
  /** Text written on the chart beside this row. */
  annotation?: string;
}

interface DumbbellProps {
  rows: DumbbellRow[];
  fromLabel: string;
  toLabel: string;
  format?: (v: number) => string;
  ariaLabel: string;
}

const W = 720;
const ROW = 20;
const M = { t: 26, r: 150, b: 22, l: 150 };

/**
 * Two points per row on a shared scale: last period (hollow) and this period
 * (filled). Gray and ink unless a row carries a tone.
 */
export function Dumbbell({
  rows,
  fromLabel,
  toLabel,
  format = (v) => String(Math.round(v)),
  ariaLabel,
}: DumbbellProps) {
  const { bind, node } = useTooltip();
  const max = niceMax(Math.max(1, ...rows.map((r) => Math.max(r.to, r.from ?? 0))));
  const iw = W - M.l - M.r;
  const x = (v: number) => M.l + (iw * v) / max;
  const H = M.t + rows.length * ROW + M.b;
  const first = rows[0];

  return (
    <>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ maxWidth: W }} role="img" aria-label={ariaLabel}>
        {ticks(max).map((t) => (
          <g key={t}>
            <line x1={x(t)} x2={x(t)} y1={M.t - 6} y2={H - M.b} stroke={ed.grid} />
            <text x={x(t)} y={H - 6} textAnchor="middle" style={svgText.axis}>
              {format(t)}
            </text>
          </g>
        ))}
        {/* Direct labels on the first row instead of a legend */}
        {first && first.from !== null && (
          <>
            <text
              x={x(first.from)}
              y={M.t - 10}
              textAnchor={first.from <= first.to ? "end" : "start"}
              style={svgText.axis}
            >
              {fromLabel} ○
            </text>
            <text
              x={x(first.to)}
              y={M.t - 10}
              textAnchor={first.from !== null && first.from > first.to ? "end" : "start"}
              style={{ ...svgText.axis, fill: ed.ink }}
            >
              ● {toLabel}
            </text>
          </>
        )}
        {rows.map((r, i) => {
          const cy = M.t + i * ROW + ROW / 2;
          const color =
            r.tone === "emph" ? ed.emph : r.tone === "caution" ? ed.caution : ed.ink;
          const lo = Math.min(r.from ?? r.to, r.to);
          const hi = Math.max(r.from ?? r.to, r.to);
          const d = r.from === null ? null : r.to - r.from;
          return (
            <g
              key={r.id}
              {...bind(
                <>
                  <b>{r.label}</b>
                  <span className="num">{r.from === null ? "–" : format(r.from)}</span>{" "}
                  {fromLabel.toLowerCase()}, <span className="num">{format(r.to)}</span>{" "}
                  {toLabel.toLowerCase()}
                  {d !== null && (
                    <>
                      {" "}
                      (<span className="num">{d > 0 ? "+" : ""}{format(d)}</span>)
                    </>
                  )}
                </>,
              )}
            >
              <rect x={0} y={cy - ROW / 2} width={W} height={ROW} fill="transparent" />
              <line x1={M.l} x2={W - M.r} y1={cy} y2={cy} stroke={ed.grid} />
              <text
                x={M.l - 8}
                y={cy + 4}
                textAnchor="end"
                style={r.tone ? svgText.strong : svgText.label}
              >
                {clip(r.label, 22)}
              </text>
              <line
                x1={x(lo)}
                x2={x(hi)}
                y1={cy}
                y2={cy}
                stroke={r.tone ? color : ed.markStrong}
                strokeWidth={2}
              />
              {r.from !== null && (
                <circle
                  cx={x(r.from)}
                  cy={cy}
                  r={4}
                  fill={ed.paper}
                  stroke={r.tone ? color : ed.markStrong}
                  strokeWidth={1.5}
                />
              )}
              <circle cx={x(r.to)} cy={cy} r={4.5} fill={color} />
              <text
                x={W - M.r + 10}
                y={cy + 4}
                style={r.tone ? svgText.numStrong : svgText.num}
              >
                {format(r.to)}
                {d !== null && d !== 0 ? `  ${d > 0 ? "▲" : "▼"}${format(Math.abs(d))}` : ""}
              </text>
              {r.annotation && (
                <text
                  x={W - M.r + 62}
                  y={cy + 4}
                  style={{ ...svgText.note, fill: color, fontWeight: 600 }}
                >
                  {r.annotation}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      {node}
    </>
  );
}
